import type { Edge, Node, NodeGroup } from '../types';
import { mixHex } from './color';

/**
 * Colouring wires by where they land.
 *
 * In a dense diagram the wires that are hard to follow are not the short ones
 * between neighbours - those you take in at a glance - but the long hauls that
 * cross half the drawing, and the bundles that converge on one busy node. Both
 * are places where two wires meet and the eye has to guess which run continues
 * where. A colour difference settles it, so those are the wires coloured here;
 * a wire between two neighbours nobody else talks to keeps the neutral stroke,
 * and the drawing stays calm.
 *
 * A wire is coloured by its destination, and the destination is read in two
 * steps:
 *
 *  1. the frame it arrives in, when it comes from outside that frame. The
 *     colour is not assigned but borrowed - it is the frame's own colour, the
 *     one already on its outline and its label - so the diagram explains its
 *     own colouring and needs no legend: follow an amber wire, arrive at the
 *     amber frame.
 *
 *  2. failing that, the node it arrives at, when two or more wires arrive
 *     there. Nothing on screen carries that colour, so the hue names nothing;
 *     it only says *these wires end in the same place, and that one does not*,
 *     which is the whole job at a crossing. This is what a diagram with no
 *     frames at all falls back to, and it is why such a diagram is coloured
 *     rather than left flat.
 *
 * Both keys are facts about the graph rather than about the picture, so the
 * hues survive a re-layout, a drag and a reload. The hub colour is drawn from
 * a hash of the node's own id, which means it depends on nothing else in the
 * diagram: adding or deleting elsewhere never repaints a wire that did not
 * change. Measured on the reference flowchart, that stability is worth paying
 * a little separation for - ranking hubs by fan-in scores a point or two
 * better on any single drawing, but only a quarter of the hubs keep their
 * colour across an edit, and a palette that reshuffles when you type is worse
 * than an occasional shared hue.
 *
 * Nothing here is stored. `Edge.color` remains the author's own override and
 * always wins; this is the default underneath it.
 */

/**
 * How far the neutral stroke travels toward the destination colour. Far enough
 * to tell two runs apart at a glance, not so far that a screenful of wires
 * competes with the boxes for attention.
 */
export const TINT_MIX = 0.62;

/**
 * How many wires must arrive at a node before it is worth colouring them.
 * At two the colour has something to distinguish; at one it would be noise,
 * and every wire in the diagram would carry a hue that meant nothing.
 */
export const HUB_MIN_FAN_IN = 2;

/**
 * The hues a busy node can be given. The same set frames are imported with, so
 * a diagram that mixes the two rules does not look like two diagrams.
 */
export const HUB_PALETTE = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316'];

/** FNV-1a: a small, stable hash, so a node's colour depends only on its own id. */
function hashId(id: string): number {
    let h = 2166136261;
    for (let i = 0; i < id.length; i++) {
        h ^= id.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

/** The frame a node sits directly inside, if any. */
function frameOf(nodeId: string, nodeById: Map<string, Node>): string | undefined {
    return nodeById.get(nodeId)?.parentGroupId;
}

/**
 * The colour each edge should take, keyed by edge id. Only edges that cross a
 * frame boundary or arrive at a busy node appear; everything else is left to
 * the neutral.
 */
export function buildEdgeTints(edges: Edge[], nodes: Node[], groups: NodeGroup[]): Record<string, string> {
    const nodeById = new Map(nodes.map(n => [n.id, n]));
    const groupById = new Map(groups.map(g => [g.id, g]));

    const fanIn = new Map<string, number>();
    for (const edge of edges) fanIn.set(edge.targetNodeId, (fanIn.get(edge.targetNodeId) ?? 0) + 1);

    const tints: Record<string, string> = {};
    for (const edge of edges) {
        const from = frameOf(edge.sourceNodeId, nodeById);
        const to = frameOf(edge.targetNodeId, nodeById);
        // A wire arriving from outside a frame borrows that frame's colour.
        const frameColour = to && from !== to ? groupById.get(to)?.color : undefined;
        if (frameColour) {
            tints[edge.id] = frameColour;
            continue;
        }
        // Otherwise the node it lands on speaks, but only if it is busy enough
        // for the colour to be telling the reader something.
        if ((fanIn.get(edge.targetNodeId) ?? 0) < HUB_MIN_FAN_IN) continue;
        if (!nodeById.has(edge.targetNodeId)) continue;
        tints[edge.id] = HUB_PALETTE[hashId(edge.targetNodeId) % HUB_PALETTE.length];
    }
    return tints;
}

/** The stroke a wire is drawn with: the neutral, pulled toward its destination. */
export function tintedStroke(neutral: string, tint: string | undefined): string {
    return tint ? mixHex(neutral, tint, TINT_MIX) : neutral;
}
